import { clientEnvModule } from "@/modules/client-env";
import type { AppStore } from "@/store/AppStore";
import type { PageStore } from "@/store/PageStore";
import { localStorageModule } from "@/modules/local-storage";

export type EphemeralRouteHandler = (
  data: string,
  context: {
    store: AppStore;
    pageStore: PageStore;
  }
) => void | Promise<void>;

export interface EphemeralRouteConfig {
  path: string;
  queryParam: string;
  handler: EphemeralRouteHandler;
}

const pendingActions = new Map<string, string>();
const routeConfigs: EphemeralRouteConfig[] = [];

export const registerEphemeralRoute = (config: EphemeralRouteConfig): void => {
  routeConfigs.push(config);
};

/**
 * Handles ephemeral routes before React Router processes them.
 * Extracts query params, stores them temporarily, and replaces the URL with /.
 * This prevents ephemeral routes from appearing in browser history.
 *
 * Should be called in entry.ts before mounting the app.
 */
export const handleEphemeralRoutes = (): void => {
  const isDesktop = clientEnvModule.isDesktop();
  let currentPath: string;
  let currentSearch: string;

  // Use document.URL instead of window.location to avoid lint restrictions
  const currentUrl = new URL(document.URL);

  if (isDesktop) {
    // HashRouter - check hash
    // NOTE: this is not currently in use in the product,
    // and this code is not well tested in the wild.
    const hash = currentUrl.hash;
    const hashParts = hash.split("?");
    currentPath = hashParts[0].replace("#", "") || "/";
    currentSearch = hashParts[1] || "";
  } else {
    // BrowserRouter - check pathname
    currentPath = currentUrl.pathname;
    currentSearch = currentUrl.search;
  }

  // Check each registered route config
  for (const config of routeConfigs) {
    if (currentPath === config.path) {
      const searchParams = new URLSearchParams(currentSearch);
      const value = searchParams.get(config.queryParam);

      // Store the value if it exists
      if (value) {
        const decodedValue = decodeURIComponent(value).trim();
        pendingActions.set(config.path, decodedValue);
        // Persist to localStorage so it survives page reloads during login flow
        localStorageModule
          .writePendingEphemeralRouteAction({
            path: config.path,
            data: decodedValue,
          })
          .catch(() => {
            // Silently fail - localStorage write errors are logged internally
          });
      }

      // Extract any parameters that start with utm_ before replacing the URL
      const utmParams = new URLSearchParams();
      for (const [key, value] of searchParams.entries()) {
        if (key.startsWith("utm_")) {
          utmParams.set(key, value);
        }
      }

      // Replace the URL with / before React Router sees it
      // Preserve any parameters starting with utm_ so they're available for tracking
      const utmString = utmParams.toString();
      const newUrl = utmString ? `/?${utmString}` : "/";

      if (isDesktop) {
        window.history.replaceState(null, "", `#${newUrl}`);
      } else {
        window.history.replaceState(null, "", newUrl);
      }

      // Only handle the first matching route
      break;
    }
  }
};

/**
 * Gets and clears all pending ephemeral route actions.
 * Returns a map of route path to handler function and data.
 * Should be called once when stores are ready to process the actions.
 * Merges in-memory actions with localStorage actions (for login flow persistence).
 */
export const getAndClearPendingActions = async (): Promise<
  Array<{
    config: EphemeralRouteConfig;
    data: string;
  }>
> => {
  const actions: Array<{ config: EphemeralRouteConfig; data: string }> = [];

  // First, collect in-memory actions
  for (const config of routeConfigs) {
    const data = pendingActions.get(config.path);
    if (data) {
      actions.push({ config, data });
      pendingActions.delete(config.path);
    }
  }

  // Then, check localStorage for persisted actions (e.g., after login)
  const persistedAction = await localStorageModule.readPendingEphemeralRouteAction();
  if (persistedAction) {
    // Find the matching config
    const config = routeConfigs.find(c => c.path === persistedAction.path);
    if (config) {
      // Only add if we don't already have an in-memory action for this path
      const hasInMemoryAction = actions.some(a => a.config.path === persistedAction.path);
      if (!hasInMemoryAction) {
        actions.push({ config, data: persistedAction.data });
      }
    }
    // Clear localStorage after reading
    await localStorageModule.clearPendingEphemeralRouteAction();
  }

  return actions;
};
